import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, Avatar, TextField, Button,
  Paper, Alert, CircularProgress, Badge,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { getMe, updateProfile, getProfileUploadUrl, uploadToBlob } from '../services/api';

const COLOR_OPTIONS = [
  '#1565c0', '#e91e63', '#9c27b0', '#4caf50', '#ff6b35',
  '#f44336', '#00bcd4', '#795548', '#607d8b', '#ff9800',
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [displayName, setDisplayName] = useState('');
  const [avatarColor, setAvatarColor] = useState(COLOR_OPTIONS[0]);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await getMe();
      setDisplayName(data.displayName || data.username || user?.userDetails || '');
      setAvatarColor(data.avatarColor || COLOR_OPTIONS[0]);
      setProfilePhotoUrl(data.profilePhotoUrl || null);
    } catch {
      setDisplayName(user?.userDetails || '');
    } finally {
      setLoading(false);
    }
  }, [user?.userDetails]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const initials = displayName
    ? displayName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : (user?.userDetails?.[0]?.toUpperCase() || 'U');

  const handlePhotoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setUploading(true);
    try {
      const { data } = await getProfileUploadUrl();
      await uploadToBlob(data.uploadUrl, file);
      await updateProfile({ profilePhotoUrl: data.blobUrl });
      setProfilePhotoUrl(data.blobUrl);
      setUser((prev) => ({ ...prev, profilePhotoUrl: data.blobUrl }));
    } catch {
      setError('Failed to upload photo.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [setUser]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await updateProfile({ displayName, avatarColor });
      setUser((prev) => ({ ...prev, displayName, avatarColor, profilePhotoUrl }));
      navigate('/');
    } catch {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Profile</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Box display="flex" justifyContent="center" mb={3}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handlePhotoSelect}
          />
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              uploading ? (
                <CircularProgress size={24} />
              ) : (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'grey.700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: '2px solid white',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CameraAltIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
              )
            }
          >
            <Avatar
              src={profilePhotoUrl || undefined}
              sx={{
                width: 80,
                height: 80,
                bgcolor: avatarColor,
                fontSize: 32,
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {profilePhotoUrl ? null : initials}
            </Avatar>
          </Badge>
        </Box>

        <Box component="form" onSubmit={handleSave}>
          <TextField
            label="Display Name"
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" gutterBottom>Avatar Color</Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
            {COLOR_OPTIONS.map((color) => (
              <Box
                key={color}
                onClick={() => setAvatarColor(color)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: color,
                  cursor: 'pointer',
                  border: avatarColor === color ? '3px solid' : '3px solid transparent',
                  borderColor: avatarColor === color ? 'text.primary' : 'transparent',
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'scale(1.15)' },
                }}
              />
            ))}
          </Box>

          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
            fullWidth
            size="large"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
